import { useApolloClient } from "@apollo/client";
import { useEffect, useState } from "react";
import { DocumentStorageStatus } from "src/hooks/useDocumentStorage";
import { useMessageRichContent } from "src/hooks/useMessageRichContent";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { RichContentDocument } from "src/types/messages/content";
import * as RD from "src/types/remoteData";
import { GET_MESSAGE_TEMPLATE_AND_RICH_CONTENT } from "../graphql/queries";

export type RichContentDocumentWithContentId = RichContentDocument & {
  content_id?: string;
};

export type MessageTemplateWithRichContents = {
  formTemplateId: string;
  enrollmentPeriodId: string;
  templateType: GQL.message_template_type_enum;
  schoolId: string | null;
  template: GQL.GetMessageTemplateAndRichContent;
  richContents: RichContentDocumentWithContentId[];
  schoolSpecificEnabled: boolean;
};

export function useMessageTemplateRemoteData(): {
  remoteData: RD.RemoteData<Error, MessageTemplateWithRichContents>;
} {
  const params = Url.OrgAdmin.MessageTemplate.useParams();
  const {
    formTemplateId = "",
    enrollmentPeriodId = "",
    messageTemplateType = "",
    schoolId: schoolIdParam = "",
  } = params;

  const schoolId = schoolIdParam === "standard" ? null : schoolIdParam;
  const templateType = messageTemplateType as GQL.message_template_type_enum;

  const apolloClient = useApolloClient();
  const { downloadRichContent } = useMessageRichContent();

  const [remoteData, setRemoteData] = useState<
    RD.RemoteData<Error, MessageTemplateWithRichContents>
  >(RD.notAsked());

  useEffect(() => {
    if (!formTemplateId || !enrollmentPeriodId || !templateType) {
      return;
    }

    setRemoteData((prev) => RD.loading(prev));

    (async () => {
      try {
        const result = await apolloClient.query<
          GQL.GetMessageTemplateAndRichContent,
          GQL.GetMessageTemplateAndRichContentVariables
        >({
          query: GET_MESSAGE_TEMPLATE_AND_RICH_CONTENT,
          variables: {
            formTemplateId: formTemplateId,
            templateType,
            schoolId: schoolId ?? "",
            skipSchool: !schoolId,
          },
          fetchPolicy: "cache-first",
        });

        // Extract all content_ids from the template
        const schoolSpecificEnabled =
          !!schoolId &&
          !!result.data.form_template_by_pk?.school_specific_messages_enabled;

        const messageTemplate = schoolSpecificEnabled
          ? result.data.message_template_school_specific[0]
          : result.data.message_template_standard[0];

        const richContentRows =
          messageTemplate?.message_template_rich_contents ?? [];

        // Download all rich contents in parallel
        if (richContentRows.length === 0) {
          setRemoteData(
            RD.success<Error, MessageTemplateWithRichContents>({
              formTemplateId,
              enrollmentPeriodId,
              templateType,
              schoolId,
              template: result.data,
              richContents: [],
              schoolSpecificEnabled,
            })
          );
          return;
        }

        const results = await Promise.allSettled(
          richContentRows.map((row) => downloadRichContent(row.content_id))
        );

        const richContents: RichContentDocumentWithContentId[] = [];
        let hasLoading = false;
        const errors: Error[] = [];

        for (let i = 0; i < results.length; i++) {
          const settled = results[i];
          const row = richContentRows[i];
          if (!settled || !row) continue;

          if (settled.status === "fulfilled") {
            if (settled.value.status === DocumentStorageStatus.OK) {
              richContents.push({
                ...settled.value.data,
                content_id: row.content_id,
              });
            } else if (settled.value.status === DocumentStorageStatus.LOADING) {
              hasLoading = true;
            } else {
              errors.push(new Error("Failed to download rich content"));
            }
          } else {
            errors.push(
              settled.reason instanceof Error
                ? settled.reason
                : new Error(String(settled.reason))
            );
          }
        }

        // If any are still loading, keep RemoteData in loading state
        // The effect will re-run and retry (due to dependency changes or manual trigger)
        if (hasLoading) {
          setRemoteData((prev) => RD.loading(prev));
          return;
        }

        // If all failed, set error
        if (richContents.length === 0 && errors.length > 0) {
          setRemoteData((prev) =>
            RD.failure(
              errors[0] ?? new Error("Failed to download rich contents"),
              prev
            )
          );
          return;
        }

        setRemoteData(
          RD.success<Error, MessageTemplateWithRichContents>({
            formTemplateId,
            enrollmentPeriodId,
            templateType,
            schoolId,
            template: result.data,
            richContents,
            schoolSpecificEnabled,
          })
        );
      } catch (error) {
        const errorObj: Error =
          error instanceof Error ? error : new Error(String(error));
        setRemoteData((prev) => RD.failure(errorObj, prev));
      }
    })();
  }, [
    formTemplateId,
    templateType,
    schoolId,
    apolloClient,
    downloadRichContent,
    enrollmentPeriodId,
  ]);

  return { remoteData };
}
